// Variables Table for Template Builder:
// Meta requires sample values for all {{n}} variables before submitting for review.
// Also allows mapping each variable to its dynamic subscriber data source.

import { useI18n } from '../../lib/i18n';
import {
  COMMON_CUSTOM_FIELDS,
  SYSTEM_VARIABLES,
  type WaTemplateVariable,
  type WaTemplateVariableSource,
} from '../../lib/templateTypes';

export interface VariablesTableProps {
  variables: WaTemplateVariable[];
  onChange: (variables: WaTemplateVariable[]) => void;
  disabled?: boolean;
}

export default function VariablesTable({
  variables,
  onChange,
  disabled = false,
}: VariablesTableProps) {
  const { t } = useI18n();

  if (variables.length === 0) {
    return null;
  }

  function updateVariable(index: number, patch: Partial<WaTemplateVariable>) {
    const updated = variables.map((v) => (v.index === index ? { ...v, ...patch } : v));
    onChange(updated);
  }

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/50 p-3.5">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
            {t('tpl.vars.title') || 'Template Variables & Examples'}
          </h4>
          <p className="text-[11px] text-slate-500">
            {t('tpl.vars.hint') ||
              'Meta requires sample values for review. In broadcasts, these will be dynamically filled.'}
          </p>
        </div>
        <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">
          {variables.length} {variables.length === 1 ? 'Variable' : 'Variables'}
        </span>
      </div>

      <div className="space-y-2.5">
        {variables.map((v) => (
          <div
            key={v.index}
            className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm sm:flex-row sm:items-center"
          >
            {/* Variable Tag */}
            <div className="flex items-center gap-2 sm:w-24">
              <span className="rounded bg-indigo-50 px-2 py-1 font-mono text-xs font-bold text-indigo-700 border border-indigo-200">
                {`{{${v.index}}}`}
              </span>
            </div>

            {/* Source Selection */}
            <div className="flex-1 min-w-[140px]">
              <label className="block text-[10px] font-medium text-slate-400 mb-0.5">
                {t('tpl.vars.source') || 'Data Source'}
              </label>
              <select
                value={v.source}
                disabled={disabled}
                onChange={(e) =>
                  updateVariable(v.index, {
                    source: e.target.value as WaTemplateVariableSource,
                  })
                }
                className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                <optgroup label="Contact Attributes">
                  <option value="name">{t('tpl.vars.srcName') || 'Subscriber Name'}</option>
                  <option value="phone">{t('tpl.vars.srcPhone') || 'Subscriber Phone'}</option>
                  <option value="static">{t('tpl.vars.srcStatic') || 'Static / Manual'}</option>
                </optgroup>

                <optgroup label="Custom Fields">
                  {COMMON_CUSTOM_FIELDS.map((cf) => (
                    <option key={cf.key} value={cf.key}>
                      {cf.label}
                    </option>
                  ))}
                </optgroup>

                <optgroup label="System / WP Variables">
                  {SYSTEM_VARIABLES.map((sv) => (
                    <option key={sv.key} value={sv.key}>
                      {sv.label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* Sample / Example Input */}
            <div className="flex-1 min-w-[140px]">
              <div className="flex justify-between items-center mb-0.5">
                <label className="block text-[10px] font-medium text-slate-400">
                  {t('tpl.vars.example') || 'Sample Example (Required)'}
                </label>
                {!v.example && (
                  <span className="text-[10px] font-bold text-amber-600">
                    * Required
                  </span>
                )}
              </div>
              <input
                type="text"
                value={v.example}
                disabled={disabled}
                placeholder={
                  v.source === 'name'
                    ? 'e.g. Sarah'
                    : v.source.includes('discount')
                    ? 'e.g. 20%'
                    : v.source.includes('order')
                    ? 'e.g. ORD-1049'
                    : 'Sample value'
                }
                onChange={(e) => updateVariable(v.index, { example: e.target.value })}
                className={`w-full rounded-md border px-2.5 py-1.5 text-xs outline-none transition-colors ${
                  !v.example
                    ? 'border-amber-300 bg-amber-50/30 focus:border-amber-500 focus:ring-1 focus:ring-amber-500'
                    : 'border-slate-200 bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'
                }`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
